import { Input } from "@/components/ui/shadcn/Input/Input";
import { Textarea } from "@/components/ui/shadcn/TextArea/Textarea";
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";

type InlineEditableFieldProps = {
  isEditing: boolean;
  value: string;
  displayValue: string;
  canEdit: boolean;
  ariaLabel: string;
  displayClassName?: string;
  inputWidthClass: string;
  autoSize?: boolean;
  textClassName?: string;
  matchDisplaySize?: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onKeyDown: (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
};

type InlineEditableNumberFieldProps = {
  isEditing: boolean;
  value: number | null;
  displayValue: string;
  canEdit: boolean;
  ariaLabel: string;
  displayClassName?: string;
  inputWidthClass: string;
  autoSize?: boolean;
  textClassName?: string;
  min?: number;
  max?: number;
  step?: number;
  onStartEdit: () => void;
  onChange: (value: number | null) => void;
  onSubmit: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export function InlineEditableField({
  isEditing,
  value,
  displayValue,
  canEdit,
  ariaLabel,
  displayClassName,
  inputWidthClass,
  autoSize = false,
  textClassName = "text-sm",
  matchDisplaySize = false,
  onStartEdit,
  onChange,
  onSubmit,
  onKeyDown,
}: InlineEditableFieldProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const displayRef = useRef<HTMLSpanElement | null>(null);
  const [displaySize, setDisplaySize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [displayStyles, setDisplayStyles] = useState<{
    fontSize: string;
    lineHeight: string;
    lineHeightPx: number | null;
    fontWeight: string;
    fontFamily: string;
    letterSpacing: string;
  } | null>(null);
  const autoSizeTarget =
    value.trim().length > 0 ? value : displayValue.trim() || " ";

  const updateDisplaySize = () => {
    const element = displayRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const computed = window.getComputedStyle(element);
    setDisplaySize({ width: rect.width, height: rect.height });
    const lineHeightPx = Number.parseFloat(computed.lineHeight);
    setDisplayStyles({
      fontSize: computed.fontSize,
      lineHeight: computed.lineHeight,
      lineHeightPx: Number.isNaN(lineHeightPx) ? null : lineHeightPx,
      fontWeight: computed.fontWeight,
      fontFamily: computed.fontFamily,
      letterSpacing: computed.letterSpacing,
    });
  };

  useLayoutEffect(() => {
    if (isEditing) return;
    updateDisplaySize();

    const element = displayRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      updateDisplaySize();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [isEditing, displayValue]);

  useLayoutEffect(() => {
    if (!isEditing) return;
    const element = matchDisplaySize ? textareaRef.current : inputRef.current;
    if (!element) return;
    const length = element.value.length;
    element.setSelectionRange(length, length);
  }, [isEditing, matchDisplaySize]);

  if (!isEditing) {
    return (
      <span
        ref={displayRef}
        role="button"
        tabIndex={0}
        className={`hover:bg-muted -mx-1 inline-block rounded-sm p-1.5 transition-colors ${displayClassName ?? ""}`}
        onClick={() => {
          if (!canEdit) return;
          updateDisplaySize();
          onStartEdit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && canEdit) {
            updateDisplaySize();
            onStartEdit();
          }
        }}
      >
        {displayValue}
      </span>
    );
  }

  const lineHeightBoost = 2;
  const lineCount =
    displayStyles?.lineHeightPx && displaySize
      ? Math.max(1, Math.round(displaySize.height / displayStyles.lineHeightPx))
      : 1;
  const extraHeight = lineCount * lineHeightBoost;

  const matchingStyle: CSSProperties | undefined =
    matchDisplaySize && displaySize
      ? {
          width: `${Math.ceil(displaySize.width + 12)}px`,
          height: `${Math.ceil(displaySize.height + extraHeight)}px`,
          ...(displayStyles ?? {}),
          lineHeight: displayStyles?.lineHeightPx
            ? `${displayStyles.lineHeightPx + lineHeightBoost}px`
            : displayStyles?.lineHeight,
        }
      : undefined;

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block align-middle"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (nextTarget && wrapperRef.current?.contains(nextTarget)) return;
        onSubmit();
      }}
    >
      {matchDisplaySize && displaySize ? (
        <Textarea
          ref={textareaRef}
          autoFocus
          value={value}
          aria-label={ariaLabel}
          rows={1}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onKeyDown={onKeyDown}
          className={`resize-none overflow-hidden font-normal ${textClassName} border-border/40 min-h-0 rounded-sm px-1.5 py-1.5 leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none`}
          style={matchingStyle}
        />
      ) : (
        <Input
          ref={inputRef}
          autoFocus
          value={value}
          aria-label={ariaLabel}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onKeyDown={onKeyDown}
          className={`h-6 ${inputWidthClass} px-1 py-0 ${textClassName} border-border/40 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none`}
          style={
            autoSize
              ? { width: `${Math.max(autoSizeTarget.length, 1) + 0.5}ch` }
              : undefined
          }
        />
      )}
    </div>
  );
}

export function InlineEditableNumberField({
  isEditing,
  value,
  displayValue,
  canEdit,
  ariaLabel,
  displayClassName,
  inputWidthClass,
  autoSize = false,
  textClassName = "text-sm",
  min,
  max,
  step,
  onStartEdit,
  onChange,
  onSubmit,
  onKeyDown,
}: InlineEditableNumberFieldProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const autoSizeTarget =
    value !== null && `${value}`.trim().length > 0
      ? `${value}`
      : displayValue.trim() || " ";
  const minAutoSizeCharacters = 6;

  if (!isEditing) {
    return (
      <span
        role="button"
        tabIndex={0}
        className={`hover:bg-muted -mx-1 inline-block rounded-sm px-1 transition-colors ${displayClassName ?? ""}`}
        onClick={() => {
          if (!canEdit) return;
          onStartEdit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && canEdit) {
            onStartEdit();
          }
        }}
      >
        {displayValue}
      </span>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block align-middle"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (nextTarget && wrapperRef.current?.contains(nextTarget)) return;
        onSubmit();
      }}
    >
      <Input
        autoFocus
        type="number"
        inputMode="decimal"
        value={value ?? ""}
        aria-label={ariaLabel}
        min={min}
        max={max}
        step={step}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (nextValue === "") {
            onChange(null);
            return;
          }
          const parsed = Number(nextValue);
          if (Number.isNaN(parsed)) return;
          onChange(parsed);
        }}
        onKeyDown={onKeyDown}
        className={`h-6 ${inputWidthClass} px-1 py-0 ${textClassName} border-border/40 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none`}
        style={
          autoSize
            ? {
                width: `${Math.max(autoSizeTarget.length, minAutoSizeCharacters) + 0.75}ch`,
              }
            : undefined
        }
      />
    </div>
  );
}
